import type { ApiResult } from "@cold-crew/shared";

// ─── Base URL ─────────────────────────────────────────────────────────────────

const BASE_URL = import.meta.env.VITE_API_URL ?? "/api";

// ─── Token storage — localStorage ────────────────────────────────────────────
// localStorage persists across browser closes (30-day session).
// The raw token never touches the server logs — only sent in Authorization header.

const TOKEN_KEY = "ccm_token";

let _token: string | null = localStorage.getItem(TOKEN_KEY);

export const tokenStore = {
  get: (): string | null => _token,

  set: (t: string): void => {
    _token = t;
    localStorage.setItem(TOKEN_KEY, t);
  },

  clear: (): void => {
    _token = null;
    localStorage.removeItem(TOKEN_KEY);
  },
};

// ─── Core request ─────────────────────────────────────────────────────────────

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?:   unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, params, ...init } = options;

  // Build URL with optional query params
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    });
  }

  // Headers — always attach token if available
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string>),
  };
  if (_token) headers["Authorization"] = `Bearer ${_token}`;

  const res = await fetch(url.toString(), {
    ...init,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

 // 401: login endpoint → leer mensaje real del body
  //      otras rutas    → limpiar sesión y redirigir
  if (res.status === 401) {
    if (path.includes("/auth/login")) {
      const json = await res.json().catch(() => ({})) as any;
      throw new Error(json?.error?.message ?? "Contraseña incorrecta");
    }
    tokenStore.clear();
    window.location.href = "/login";
    throw new Error("Sesión expirada");
  }

  const json = (await res.json()) as ApiResult<T>;

  if (!json.success) {
    const err = new Error(json.error.message) as Error & { code?: string };
    err.code  = json.error.code;
    throw err;
  }

  return json.data;
}

// ─── HTTP methods ─────────────────────────────────────────────────────────────

export const api = {
  get:    <T>(path: string, params?: RequestOptions["params"]) =>
    request<T>(path, { method: "GET", params }),

  post:   <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body }),

  put:    <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body }),

  patch:  <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body }),

  delete: <T>(path: string) =>
    request<T>(path, { method: "DELETE" }),
};
